import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShopLayout } from "@/components/ShopLayout";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/format";

type CheckoutView =
  | "loading"
  | "ok"
  | "pending"
  | "processing"
  | "failed"
  | "expired"
  | "delayed"
  | "error";

const COPY: Record<
  CheckoutView,
  { eyebrow: string; title: string; body: string; tone: "wait" | "ok" | "warn" | "bad" }
> = {
  loading: {
    eyebrow: "Stripe",
    title: "Zahlung wird bestätigt",
    body: "Einen Moment, wir gleichen die Zahlung mit Stripe ab.",
    tone: "wait",
  },
  pending: {
    eyebrow: "Stripe",
    title: "Zahlung wird bestätigt",
    body: "Einen Moment, wir gleichen die Zahlung mit Stripe ab.",
    tone: "wait",
  },
  processing: {
    eyebrow: "Bank",
    title: "Die Bank prüft die Zahlung",
    body: "Die Bestellung wird abgeschlossen, sobald das Geld eingeht. Diese Seite muss nicht offen bleiben.",
    tone: "warn",
  },
  failed: {
    eyebrow: "Nicht abgeschlossen",
    title: "Zahlung fehlgeschlagen",
    body: "Es wurde nichts berechnet. Die Bestellung können Sie im Shop erneut starten.",
    tone: "bad",
  },
  expired: {
    eyebrow: "Nicht abgeschlossen",
    title: "Zahlungssitzung abgelaufen",
    body: "Es wurde nichts berechnet. Die Bestellung können Sie im Shop erneut starten.",
    tone: "warn",
  },
  delayed: {
    eyebrow: "Noch offen",
    title: "Bestätigung steht noch aus",
    body: "Sobald Stripe die Zahlung meldet, schließen wir die Bestellung ab. Vorher wird nichts verbucht.",
    tone: "warn",
  },
  error: {
    eyebrow: "Verbindung",
    title: "Status gerade nicht erreichbar",
    body: "Der Zahlungsstatus konnte nicht geladen werden. Bitte laden Sie die Seite neu.",
    tone: "warn",
  },
  ok: {
    eyebrow: "Bezahlt",
    title: "Vielen Dank",
    body: "Ihre Vorbestellung ist bezahlt. Abholartikel bereiten wir zum vereinbarten Termin vor.",
    tone: "ok",
  },
};

function StatusMark({ tone }: { tone: "wait" | "ok" | "warn" | "bad" }) {
  if (tone === "wait") {
    return (
      <span className="inline-block h-8 w-8 animate-spin rounded-full border-[3px] border-brand-100 border-t-brand-600" />
    );
  }
  const stroke = tone === "ok" ? "text-brand-700" : tone === "bad" ? "text-red-600" : "text-amber-700";
  return (
    <svg className={`h-8 w-8 ${stroke}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      {tone === "ok" ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
        />
      )}
    </svg>
  );
}

export function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const checkoutId = params.get("checkout_id");
  const [status, setStatus] = useState<CheckoutView>("loading");
  const [total, setTotal] = useState<string | null>(null);

  useEffect(() => {
    if (!checkoutId) {
      setStatus("ok");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const checkout = await api.get<{ status: string; total_amount: string }>(
          `/api/v1/public/shop/checkout/${checkoutId}`,
        );
        if (cancelled) return;
        setTotal(checkout.total_amount);
        if (checkout.status === "completed") {
          setStatus("ok");
          return;
        }
        if (checkout.status === "failed") {
          setStatus("failed");
          return;
        }
        if (checkout.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        const waiting = checkout.status === "processing" ? "processing" : "pending";
        if (attempts >= 15) {
          setStatus(waiting === "processing" ? "processing" : "delayed");
          return;
        }
        setStatus(waiting);
        window.setTimeout(() => void poll(), 2000);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [checkoutId]);

  const copy = COPY[status];
  const ring =
    copy.tone === "ok"
      ? "bg-brand-50 text-brand-700 ring-brand-100"
      : copy.tone === "bad"
        ? "bg-red-50 text-red-700 ring-red-100"
        : copy.tone === "warn"
          ? "bg-amber-50 text-amber-800 ring-amber-100"
          : "bg-white text-brand-700 ring-slate-200";

  return (
    <ShopLayout>
      <div className="shop-page flex justify-center">
        <article className="w-full max-w-lg animate-slide-up overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-card">
          <div className="border-b border-slate-100 bg-gradient-to-b from-brand-50/80 to-white px-6 pb-8 pt-10 text-center sm:px-10">
            <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-2xl ring-8 ${ring}`}>
              <StatusMark tone={copy.tone} />
            </div>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              {copy.eyebrow}
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{copy.title}</h1>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">{copy.body}</p>
            {status === "ok" && total ? (
              <p className="mt-6 text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
                {formatPrice(total)}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-3 px-6 py-6 sm:flex-row sm:justify-center sm:px-10">
            <Link to="/" className="shop-btn-primary">
              Weitere Artikel
            </Link>
            <Link to="/warenkorb" className="shop-btn-secondary">
              Warenkorb
            </Link>
          </div>
        </article>
      </div>
    </ShopLayout>
  );
}
